/**
 * Practice summary math for the VIEWERS of a dancer's practice record:
 * the family card on FamilyDancerProfile and the staff section on
 * StaffDancerView (Bobbie, 2026-08-24: parents see and mark off their own
 * dancers' practice; teachers, directors and staff see all students).
 *
 * The dancer's own portal view computes the same numbers SERVER-SIDE in
 * netlify/functions/lib/practiceLog.mjs (readPracticeLogSummary). These are
 * deliberately the same rules, not cross-imported - that module is a server
 * function and this file has to stay import-free so the tests run under
 * plain Node with no Firebase env, the same rule absenceHistory.ts and
 * alternates.ts follow. Keep the two in sync:
 *
 *   - "today" is the studio's day, America/Chicago, everywhere;
 *   - weeks start Monday (abWeekCalendar.ts's boundary);
 *   - a streak counts consecutive distinct days walked backward, and missing
 *     TODAY does not break it (the day is not over yet) - the walk starts at
 *     today only if today is logged, otherwise at yesterday.
 */

/** The fields the math needs. Structural, so it does not import a doc type. */
export interface PracticeLogLike {
  minutes: number;
  /** 'YYYY-MM-DD' practice day. */
  date: string;
  focus: string[];
  note: string;
  /** Present only when a guardian marked this off for the dancer. */
  loggedByUid?: string;
}

export interface PracticeSummary {
  streakDays: number;
  thisWeekMinutes: number;
  thisWeekDays: number;
  totalLogs: number;
}

/** Studio "today", America/Chicago, same key the dancer portal uses. */
export function studioTodayKey(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

function shiftDayKey(dayKey: string, deltaDays: number): string {
  const shifted = new Date(`${dayKey}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + deltaDays);
  return shifted.toISOString().slice(0, 10);
}

/** Monday-anchored, matching abWeekCalendar.ts's week boundary. */
export function mondayWeekStartKey(dayKey: string): string {
  const asDate = new Date(`${dayKey}T00:00:00Z`);
  const sinceMonday = (asDate.getUTCDay() + 6) % 7;
  return shiftDayKey(dayKey, -sinceMonday);
}

export function summarizePracticeLogs(
  logs: PracticeLogLike[],
  todayKey: string
): PracticeSummary {
  const loggedDayKeys = new Set<string>();
  const weekStart = mondayWeekStartKey(todayKey);
  let thisWeekMinutes = 0;
  const thisWeekDays = new Set<string>();
  for (const log of logs) {
    if (!log.date) continue;
    loggedDayKeys.add(log.date);
    if (log.date >= weekStart && log.date <= todayKey) {
      thisWeekMinutes += Number.isFinite(log.minutes) ? Math.max(0, log.minutes) : 0;
      thisWeekDays.add(log.date);
    }
  }
  let cursor = loggedDayKeys.has(todayKey) ? todayKey : shiftDayKey(todayKey, -1);
  let streakDays = 0;
  while (loggedDayKeys.has(cursor)) {
    streakDays += 1;
    cursor = shiftDayKey(cursor, -1);
  }
  return {
    streakDays,
    thisWeekMinutes,
    thisWeekDays: thisWeekDays.size,
    totalLogs: logs.length,
  };
}
